/**
 * PhoneOverlay — telefonik (§13.1): 4 zakładki.
 * (a) Pieski (b) Gierki (c) Zaproszenia (d) Ustawienia audio + reset rodzica
 */
import Phaser from 'phaser';
import {
  DOGS,
  DOG_BREED_IDS,
  DOG_COLORS,
  type DogBreedId,
  type CharacterKind,
} from '@pieskowo/shared';
import { BaseScene } from '../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../core/UiKit.js';
import { audioBus } from '../core/AudioBus.js';
import { narrator } from '../core/Narrator.js';
import { SaveLocal } from '../core/SaveLocal.js';
import { getSceneRouter } from '../core/SceneRouter.js';
import { playFx } from '../interact/fx.js';
import {
  fetchMe,
  putDog,
  putSettings,
  fetchPendingInvites,
  respondInvite,
  resetProfile,
} from './phoneApi.js';
import { GAME_ICONS, launchMinigame, listHubGames } from './minigameMap.js';

type TabId = 'dogs' | 'games' | 'invites' | 'settings';

const TAB_META: { id: TabId; icon: string; label: string }[] = [
  { id: 'dogs', icon: '🐕', label: 'Pieski' },
  { id: 'games', icon: '🎮', label: 'Gierki' },
  { id: 'invites', icon: '🏠', label: 'Razem' },
  { id: 'settings', icon: '🔊', label: 'Dźwięk' },
];

const NAME_SUGGESTIONS = ['Misia', 'Lola', 'Pusia'] as const;

type InviteCard = {
  id: string;
  requesterDogName: string;
};

export class PhoneOverlayScene extends BaseScene {
  static readonly KEY = 'PhoneOverlay';

  private tab: TabId = 'dogs';
  private contentRoot: Phaser.GameObjects.Container | null = null;
  private tabButtons: Phaser.GameObjects.Container[] = [];
  private statusText: Phaser.GameObjects.Text | null = null;

  // dogs state
  private breedIdx = 0;
  private colorIdx = 0;
  private dogName = 'Misia';
  private characterKind: CharacterKind = 'dog';
  private nameInputEl: HTMLInputElement | null = null;

  // invites
  private invites: InviteCard[] = [];
  private invitePoll: Phaser.Time.TimerEvent | null = null;

  // parent gate — prawdziwy PIN opiekuna (4–8 cyfr), nie losowa matma
  private parentHoldStart = 0;
  private parentGateOpen = false;
  private parentCodeInput: HTMLInputElement | null = null;
  private parentPinDraft = '';

  constructor() {
    super(PhoneOverlayScene.KEY);
  }

  create(): void {
    audioBus.load();
    audioBus.unlock();

    // półprzezroczyste tło — overlay
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    const dim = this.add
      .rectangle(this.gameW / 2, this.gameH / 2, this.gameW, this.gameH, 0x000000, 0.45)
      .setInteractive();
    dim.on('pointerdown', () => {
      /* block click-through */
    });

    const frameW = 560;
    const frameH = 640;
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;

    // ramka telefonu
    const bezel = this.add
      .rectangle(cx, cy, frameW + 24, frameH + 24, 0x4a3f55)
      .setStrokeStyle(4, 0x2d2a3a);
    const screen = this.add
      .rectangle(cx, cy, frameW, frameH, 0xfff8f0)
      .setStrokeStyle(3, 0xffb6c1);
    const notch = this.add.rectangle(cx, cy - frameH / 2 + 14, 120, 10, 0x4a3f55);

    const title = this.add
      .text(cx, cy - frameH / 2 + 48, 'Telefonik', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // zamknij (X)
    UiKit.button(this, {
      x: cx + frameW / 2 - 40,
      y: cy - frameH / 2 + 48,
      label: '✕',
      width: MIN_TOUCH,
      height: MIN_TOUCH,
      fill: 0xffb6c1,
      onClick: () => this.closePhone(),
    });

    // zakładki na dole
    const tabY = cy + frameH / 2 - 56;
    const tabW = 110;
    const gap = 12;
    const totalW = TAB_META.length * tabW + (TAB_META.length - 1) * gap;
    const startX = cx - totalW / 2 + tabW / 2;
    this.tabButtons = [];
    TAB_META.forEach((meta, i) => {
      const x = startX + i * (tabW + gap);
      const btn = this.makeTabButton(x, tabY, tabW, meta);
      this.tabButtons.push(btn);
    });

    this.statusText = this.add
      .text(cx, cy + frameH / 2 - 110, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    // obszar treści
    this.contentRoot = this.add.container(cx, cy - 20);
    this.registry.set('phoneFrame', { cx, cy, frameW, frameH });

    void this.bootstrapProfile();
    this.switchTab('dogs');

    this.events.on('shutdown', () => this.cleanupDom());
    this.events.on('destroy', () => this.cleanupDom());

    // E2E hook
    this.registry.set('phoneTab', this.tab);

    // keep refs for GC
    void bezel;
    void screen;
    void notch;
    void title;
  }

  private async bootstrapProfile(): Promise<void> {
    const me = await fetchMe();
    if (!me) return;
    this.dogName = me.dogName;
    this.characterKind = me.characterKind;
    const bi = DOG_BREED_IDS.indexOf(me.breed as DogBreedId);
    if (bi >= 0) this.breedIdx = bi;
    const ci = (DOG_COLORS as readonly string[]).indexOf(me.color);
    if (ci >= 0) this.colorIdx = ci;
    if (me.settings) {
      audioBus.setMusic(me.settings.musicOn);
      audioBus.setSfx(me.settings.sfxOn);
    }
    if (this.tab === 'dogs' || this.tab === 'settings') {
      this.renderTab();
    }
  }

  private makeTabButton(
    x: number,
    y: number,
    w: number,
    meta: (typeof TAB_META)[number],
  ): Phaser.GameObjects.Container {
    const h = Math.max(MIN_TOUCH, 72);
    const bg = this.add
      .rectangle(0, 0, w, h, 0xbde0fe)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const icon = this.add
      .text(0, -10, meta.icon, { fontSize: '26px' })
      .setOrigin(0.5);
    const label = this.add
      .text(0, 18, meta.label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const c = this.add.container(x, y, [bg, icon, label]);
    c.setSize(w, h);
    c.setData('tabId', meta.id);
    const fire = () => this.switchTab(meta.id);
    bg.on('pointerdown', fire);
    return c;
  }

  private switchTab(tab: TabId): void {
    this.tab = tab;
    this.registry.set('phoneTab', tab);
    this.parentGateOpen = false;
    this.cleanupDom();
    // highlight tabs
    for (const btn of this.tabButtons) {
      const id = btn.getData('tabId') as TabId;
      const bg = btn.list[0] as Phaser.GameObjects.Rectangle;
      bg.setFillStyle(id === tab ? 0xffb6c1 : 0xbde0fe);
    }
    if (tab === 'invites') {
      void this.loadInvites();
      this.invitePoll?.remove(false);
      this.invitePoll = this.time.addEvent({
        delay: 30_000,
        loop: true,
        callback: () => void this.loadInvites(),
      });
    } else {
      this.invitePoll?.remove(false);
      this.invitePoll = null;
    }
    this.renderTab();
  }

  private renderTab(): void {
    if (!this.contentRoot) return;
    this.contentRoot.removeAll(true);
    this.setStatus('');
    switch (this.tab) {
      case 'dogs':
        this.renderDogsTab();
        break;
      case 'games':
        this.renderGamesTab();
        break;
      case 'invites':
        this.renderInvitesTab();
        break;
      case 'settings':
        this.renderSettingsTab();
        break;
    }
  }

  // ─── (a) Pieski ────────────────────────────────────────────────────
  private renderDogsTab(): void {
    const root = this.contentRoot!;
    const breed = DOG_BREED_IDS[this.breedIdx]!;
    const dog = DOGS[breed];
    const color = DOG_COLORS[this.colorIdx] ?? dog.defaultColor;

    const title = this.add
      .text(0, -220, 'Twój piesek', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // duży kafel-piesek — klikalny (reakcja: podskok + serduszka)
    const swatch = this.add
      .circle(0, -100, 70, Phaser.Display.Color.HexStringToColor(color).color)
      .setStrokeStyle(4, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const emoji = this.add
      .text(0, -100, this.characterKind === 'dragon' ? '🐉' : '🐶', {
        fontSize: '56px',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const petPortrait = (): void => {
      playFx(this, 'hearts', root.x, root.y - 100);
      this.tweens.add({
        targets: [swatch, emoji],
        scaleX: 1.18,
        scaleY: 1.18,
        angle: this.characterKind === 'dragon' ? 8 : -6,
        duration: 140,
        yoyo: true,
        ease: 'Back.Out',
      });
      this.setStatus(this.characterKind === 'dragon' ? 'Skrzeek! 🔥' : 'Hau hau! 🐾');
    };
    swatch.on('pointerdown', petPortrait);
    emoji.on('pointerdown', petPortrait);
    const breedLabel = this.add
      .text(0, -20, dog.namePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const personality = this.add
      .text(0, 8, dog.personalityPl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    // prev / next
    const prev = UiKit.button(this, {
      x: -180,
      y: -100,
      label: '◀',
      width: MIN_TOUCH,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => {
        this.breedIdx =
          (this.breedIdx - 1 + DOG_BREED_IDS.length) % DOG_BREED_IDS.length;
        this.renderTab();
      },
    });
    const next = UiKit.button(this, {
      x: 180,
      y: -100,
      label: '▶',
      width: MIN_TOUCH,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => {
        this.breedIdx = (this.breedIdx + 1) % DOG_BREED_IDS.length;
        this.renderTab();
      },
    });

    // kolory
    const colorRow: Phaser.GameObjects.GameObject[] = [];
    DOG_COLORS.forEach((hex: string, i: number) => {
      const x = -140 + i * 40;
      const c = this.add
        .circle(x, 50, 16, Phaser.Display.Color.HexStringToColor(hex).color)
        .setStrokeStyle(i === this.colorIdx ? 4 : 2, i === this.colorIdx ? 0x4a3f55 : 0xffffff)
        .setInteractive({ useHandCursor: true });
      c.on('pointerdown', () => {
        this.colorIdx = i;
        this.renderTab();
      });
      colorRow.push(c);
    });

    // piesek / smok
    const kindDog = UiKit.button(this, {
      x: -90,
      y: 100,
      label: '🐶 Piesek',
      width: 140,
      height: MIN_TOUCH,
      fill: this.characterKind === 'dog' ? 0xffb6c1 : 0xe6e0f8,
      onClick: () => {
        this.characterKind = 'dog';
        this.renderTab();
      },
    });
    const kindDragon = UiKit.button(this, {
      x: 90,
      y: 100,
      label: '🐉 Smok',
      width: 140,
      height: MIN_TOUCH,
      fill: this.characterKind === 'dragon' ? 0xffb6c1 : 0xe6e0f8,
      onClick: () => {
        this.characterKind = 'dragon';
        this.renderTab();
      },
    });

    // sugestie imion
    const nameLabel = this.add
      .text(0, 150, `Imię: ${this.dogName}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
      })
      .setOrigin(0.5);

    NAME_SUGGESTIONS.forEach((n, i) => {
      const b = UiKit.button(this, {
        x: -140 + i * 140,
        y: 200,
        label: n,
        width: 120,
        height: 56,
        fill: this.dogName === n ? 0xffb6c1 : 0xbde0fe,
        onClick: () => {
          this.dogName = n;
          this.renderTab();
        },
      });
      root.add(b);
    });

    // DOM input imienia
    this.mountNameInput();

    const save = UiKit.button(this, {
      x: 0,
      y: 270,
      label: 'Zapisz ✨',
      width: 200,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => void this.saveDog(),
    });

    root.add([
      title,
      swatch,
      emoji,
      breedLabel,
      personality,
      prev,
      next,
      ...colorRow,
      kindDog,
      kindDragon,
      nameLabel,
      save,
    ]);
  }

  private mountNameInput(): void {
    this.cleanupNameInput();
    const parent = this.game.canvas.parentElement;
    if (!parent) return;
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 12;
    input.value = this.dogName;
    input.placeholder = 'Imię pieska';
    input.setAttribute('aria-label', 'Imię pieska');
    input.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:58%',
      'transform:translate(-50%,-50%)',
      'width:200px',
      'height:44px',
      'font-size:18px',
      'text-align:center',
      'border:3px solid #ffb6c1',
      'border-radius:12px',
      'background:#fff',
      'color:#4a3f55',
      'z-index:20',
      'font-family:system-ui,sans-serif',
    ].join(';');
    input.addEventListener('input', () => {
      this.dogName = input.value.slice(0, 12);
    });
    parent.style.position = parent.style.position || 'relative';
    parent.appendChild(input);
    this.nameInputEl = input;
  }

  private cleanupNameInput(): void {
    this.nameInputEl?.remove();
    this.nameInputEl = null;
  }

  private async saveDog(): Promise<void> {
    if (this.nameInputEl) this.dogName = this.nameInputEl.value.trim() || this.dogName;
    const breed = DOG_BREED_IDS[this.breedIdx]!;
    const color = DOG_COLORS[this.colorIdx] ?? DOGS[breed].defaultColor;
    // lustro lokalne (offline-friendly)
    SaveLocal.setJson('pieskowo.dogDraft', {
      dogName: this.dogName,
      breed,
      color,
      characterKind: this.characterKind,
    });
    const res = await putDog({
      dogName: this.dogName,
      breed,
      color,
      characterKind: this.characterKind,
    });
    if (res) {
      this.dogName = res.profile.dogName;
      this.setStatus('Zapisano! 💖');
    } else {
      this.setStatus('Zapisano lokalnie (offline)');
    }
    this.renderTab();
  }

  // ─── (b) Gierki ────────────────────────────────────────────────────
  private renderGamesTab(): void {
    const root = this.contentRoot!;
    const title = this.add
      .text(0, -220, 'Gierki', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    root.add(title);

    const games = listHubGames('phone');
    const cols = 2;
    const tileW = 200;
    const tileH = 100;
    games.forEach((g, i: number) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = -110 + col * 220;
      const y = -120 + row * 120;
      const bg = this.add
        .rectangle(x, y, tileW, tileH, 0xb8e6d0)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const icon = this.add
        .text(x, y - 18, GAME_ICONS[g.id], { fontSize: '36px' })
        .setOrigin(0.5);
      const label = this.add
        .text(x, y + 28, g.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        this.tweens.add({
          targets: bg,
          scaleX: 0.95,
          scaleY: 0.95,
          duration: 80,
          yoyo: true,
          onComplete: () => launchMinigame(g.id, 'phone'),
        });
      });
      root.add([bg, icon, label]);
    });
  }

  // ─── (c) Zaproszenia ───────────────────────────────────────────────
  private async loadInvites(): Promise<void> {
    const data = await fetchPendingInvites();
    this.invites =
      data?.invites.map((inv) => ({
        id: inv.id,
        requesterDogName: inv.requesterDogName,
      })) ?? [];
    if (this.tab === 'invites') this.renderTab();
  }

  private renderInvitesTab(): void {
    const root = this.contentRoot!;
    const title = this.add
      .text(0, -220, 'Zamieszkaj razem', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    root.add(title);

    if (this.invites.length === 0) {
      const empty = this.add
        .text(0, -40, 'Brak próśb 🐾\nTu pojawią się zaproszenia', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#6b5f7a',
          align: 'center',
        })
        .setOrigin(0.5);
      root.add(empty);
      return;
    }

    this.invites.slice(0, 3).forEach((inv, i) => {
      const y = -130 + i * 130;
      const card = this.add
        .rectangle(0, y, 420, 110, 0xe6e0f8)
        .setStrokeStyle(3, 0xffffff);
      const name = this.add
        .text(0, y - 28, `🐶 ${inv.requesterDogName}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const yes = UiKit.button(this, {
        x: -90,
        y: y + 28,
        label: 'Tak ✓',
        width: 140,
        height: MIN_TOUCH,
        fill: 0xb8e6d0,
        onClick: () => void this.onInviteRespond(inv.id, true),
      });
      const no = UiKit.button(this, {
        x: 90,
        y: y + 28,
        label: 'Nie',
        width: 140,
        height: MIN_TOUCH,
        fill: 0xffb6c1,
        onClick: () => void this.onInviteRespond(inv.id, false),
      });
      root.add([card, name, yes, no]);
    });
  }

  private async onInviteRespond(id: string, accept: boolean): Promise<void> {
    const status = await respondInvite(id, accept);
    if (status) {
      this.setStatus(accept ? 'Witamy w domku! 🏠' : 'Odrzucono prośbę');
      this.invites = this.invites.filter((i) => i.id !== id);
    } else {
      this.setStatus('Spróbuj później');
    }
    this.renderTab();
  }

  // ─── (d) Ustawienia ────────────────────────────────────────────────
  private renderSettingsTab(): void {
    const root = this.contentRoot!;
    narrator.load();
    const title = this.add
      .text(0, -220, 'Ustawienia dźwięku', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    root.add(title);

    // MUZYKA
    this.addToggleRow(
      root,
      -110,
      audioBus.musicOn ? '🎵 Muzyka: WŁ.' : '🎵 Muzyka: WYŁ.',
      audioBus.musicOn,
      0xb8e6d0,
      0xd0d0d0,
      () => {
        audioBus.setMusic(!audioBus.musicOn);
        void putSettings(audioBus.musicOn, audioBus.sfxOn);
        this.registry.set('audioMusic', audioBus.musicOn);
        this.renderTab();
      },
    );

    // SFX
    this.addToggleRow(
      root,
      -20,
      audioBus.sfxOn ? '🔔 Dźwięki: WŁ.' : '🔔 Dźwięki: WYŁ.',
      audioBus.sfxOn,
      0xbde0fe,
      0xd0d0d0,
      () => {
        audioBus.setSfx(!audioBus.sfxOn);
        void putSettings(audioBus.musicOn, audioBus.sfxOn);
        this.registry.set('audioSfx', audioBus.sfxOn);
        this.renderTab();
      },
    );

    // LEKTOR (G15) — flaga Narrator.lektorOn, localStorage pieskowo.lektor
    this.addToggleRow(
      root,
      70,
      narrator.isOn ? '🗣️ Lektor: WŁ.' : '🗣️ Lektor: WYŁ.',
      narrator.isOn,
      0xffd6a5,
      0xd0d0d0,
      () => {
        narrator.setLektor(!narrator.isOn);
        this.registry.set('lektorOn', narrator.isOn);
        // potwierdzenie stanu głosem (gdy właśnie włączono)
        if (narrator.isOn) {
          narrator.say('ui_narrator_on', { immediate: true });
        }
        this.renderTab();
      },
    );

    // bramka rodzica — reset
    if (!this.parentGateOpen) {
      const parentLabel = this.add
        .text(0, 155, 'Rodzic: przytrzymaj 5 s', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5);
      const holdBg = this.add
        .rectangle(0, 215, 200, MIN_TOUCH, 0xe6e0f8)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      // pasek wypełnienia — widoczny postęp przytrzymania (feedback podczas 5 s)
      const holdFill = this.add
        .rectangle(-97, 215, 194, MIN_TOUCH - 6, 0xffb6c1)
        .setOrigin(0, 0.5);
      holdFill.scaleX = 0;
      const holdTxt = this.add
        .text(0, 215, '👨‍👩‍👧 Wyczyść profil', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const resetHoldFill = () => {
        this.tweens.killTweensOf(holdFill);
        holdFill.scaleX = 0;
      };
      holdBg.on('pointerdown', () => {
        this.parentHoldStart = this.time.now;
        resetHoldFill();
        this.tweens.add({ targets: holdFill, scaleX: 1, duration: 5000 });
      });
      holdBg.on('pointerup', () => {
        const held = this.time.now - this.parentHoldStart;
        resetHoldFill();
        if (held >= 5000) {
          this.openParentGate();
        } else {
          this.setStatus(`Jeszcze ${Math.ceil((5000 - held) / 1000)} s…`);
        }
      });
      holdBg.on('pointerout', () => {
        this.parentHoldStart = 0;
        resetHoldFill();
      });
      root.add([parentLabel, holdBg, holdFill, holdTxt]);
    } else {
      this.renderParentGate(root);
    }
  }

  private addToggleRow(
    root: Phaser.GameObjects.Container,
    y: number,
    label: string,
    on: boolean,
    onColor: number,
    offColor: number,
    onClick: () => void,
  ): void {
    const bg = this.add
      .rectangle(0, y, 360, MIN_TOUCH + 8, on ? onColor : offColor)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const txt = this.add
      .text(0, y, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    bg.on('pointerdown', () => {
      this.tweens.add({
        targets: bg,
        scaleX: 0.96,
        scaleY: 0.96,
        duration: 80,
        yoyo: true,
        onComplete: onClick,
      });
    });
    root.add([bg, txt]);
  }

  private openParentGate(): void {
    this.parentGateOpen = true;
    this.parentPinDraft = '';
    this.renderTab();
  }

  private renderParentGate(root: Phaser.GameObjects.Container): void {
    const info = this.add
      .text(
        0,
        60,
        'PIN opiekuna (4–8 cyfr)\nUsuwa CAŁE konto i wszystkie pieski\n(nie tylko jednego)',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
          align: 'center',
        },
      )
      .setOrigin(0.5);
    root.add(info);

    this.cleanupParentInput();
    const parent = this.game.canvas.parentElement;
    if (parent) {
      const input = document.createElement('input');
      input.type = 'password';
      input.inputMode = 'numeric';
      input.autocomplete = 'one-time-code';
      input.maxLength = 8;
      input.placeholder = '••••';
      input.setAttribute('aria-label', 'PIN opiekuna');
      input.pattern = '[0-9]*';
      input.style.cssText = [
        'position:absolute',
        'left:50%',
        'top:58%',
        'transform:translate(-50%,-50%)',
        'width:140px',
        'height:52px',
        'font-size:28px',
        'letter-spacing:6px',
        'text-align:center',
        'border:3px solid #4a3f55',
        'border-radius:12px',
        'z-index:21',
      ].join(';');
      parent.appendChild(input);
      this.parentCodeInput = input;
    }

    const confirm = UiKit.button(this, {
      x: 0,
      y: 230,
      label: 'Usuń całe konto',
      width: 260,
      height: MIN_TOUCH,
      fill: 0xffb6c1,
      onClick: () => void this.confirmReset(),
    });
    const cancel = UiKit.button(this, {
      x: 0,
      y: 300,
      label: 'Anuluj',
      width: 160,
      height: 56,
      fill: 0xbde0fe,
      onClick: () => {
        this.parentGateOpen = false;
        this.parentPinDraft = '';
        this.cleanupParentInput();
        this.renderTab();
      },
    });
    root.add([confirm, cancel]);
  }

  private async confirmReset(): Promise<void> {
    const typed = (this.parentCodeInput?.value ?? this.parentPinDraft).trim();
    // PIN nie ląduje w statusie / logach — tylko walidacja długości.
    if (!/^\d{4,8}$/.test(typed)) {
      this.setStatus('PIN: wpisz 4–8 cyfr');
      return;
    }
    const result = await resetProfile(typed);
    // wyzeruj input natychmiast
    if (this.parentCodeInput) this.parentCodeInput.value = '';
    this.parentPinDraft = '';

    if (result === 'ok') {
      SaveLocal.clearPlayerData();
      this.setStatus('Konto usunięte');
      this.closePhone();
      getSceneRouter().go('Onboarding');
      return;
    }
    if (result === 'unauthorized') {
      this.setStatus('Zły PIN — spróbuj jeszcze raz');
      return;
    }
    // offline / błąd sieci: nie usuwamy lokalnie bez potwierdzenia serwera
    // (rodzic mógł mieć zły PIN i serwer nie odpowiedział)
    this.setStatus('Brak sieci — spróbuj później');
  }

  private cleanupParentInput(): void {
    this.parentCodeInput?.remove();
    this.parentCodeInput = null;
  }

  private cleanupDom(): void {
    this.cleanupNameInput();
    this.cleanupParentInput();
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }

  private closePhone(): void {
    this.cleanupDom();
    this.invitePoll?.remove(false);
    this.invitePoll = null;
    getSceneRouter().stopOverlay(PhoneOverlayScene.KEY);
  }
}

export default PhoneOverlayScene;
